import { useCallback, useEffect, useRef } from 'react';

/**
 * Wraps the provided callback function with 2 requestAnimationFrame callback
 * wrappers to execute, the callback after at least 1 frame has been painted.
 *
 * The goal of this is to ensure that the callback doesn't incur high INP scores.
 */
const useAfterRenderCallback = <P extends any[]>(cb: (...args: P) => void):
(...args: P) => void => {
  const ref = useRef<((...args: P) => void) | null>(cb);
  ref.current = cb;

  const fn = useCallback((...args: any): void => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof ref.current === 'function') {
          ref.current(...args);
        }
      });
    });
  }, []);

  useEffect(() => () => { ref.current = null; }, []);

  return fn;
};

export default useAfterRenderCallback;
